import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { forgotPasswordApi } from "../model/authApi";
import { validateEmailWithMessage } from "../utils/auth.utils";
import { toastService } from "@/shared/toast";
import { routes } from "@/app/navigation/routes";

const RESEND_BUTTON_ID = "force-password-update-resend-button";

export const useForcePasswordUpdateViewModel = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const email = useMemo(() => {
    const fromState = (location.state as { email?: string } | null)?.email?.trim() ?? "";
    if (fromState) return fromState;
    return (sessionStorage.getItem("email") ?? "").trim();
  }, [location.state]);

  const mutation = useMutation({
    mutationFn: (targetEmail: string) => forgotPasswordApi(targetEmail, "inline", RESEND_BUTTON_ID),
    onSuccess: () => {
      toastService.show(
        "If that email is registered, we've sent instructions to reset your password.",
        "success"
      );
    },
    onError: () => {
      toastService.show("Unable to process your request right now. Please try again.", "error");
    },
  });

  const handleResendLink = useCallback(() => {
    if (!email) {
      toastService.show("We couldn't find your email. Please sign in again.", "error");
      return;
    }
    const validation = validateEmailWithMessage(email);
    if (!validation.isValid) {
      toastService.show(validation.error || "Invalid email.", "error");
      return;
    }
    mutation.mutate(email);
  }, [email, mutation]);

  const handleGoToLogin = useCallback(() => {
    navigate(routes.login, {
      state: email ? { email } : undefined,
    });
  }, [email, navigate]);

  return {
    email,
    hasEmail: Boolean(email),
    isLoading: mutation.isPending,
    handleResendLink,
    handleGoToLogin,
  };
};
